// Implements: F-UIX-019, F-UIX-007, F-GEN-036
/**
 * "Modelos de lenguaje" (UIX.8, F-GEN-036): the platform admin reads and edits the model, output
 * cap, reasoning effort and price of each tier and the message maximum. It is the content of
 * `/plataforma/modelos` (UX-11), behind `model_settings:manage` (the page mounts it on that
 * action alone): the form in the main column, the revision history beside it.
 *
 * The form starts from the revision it was loaded from and every save sends it as
 * `expected_revision`. A `409` (somebody saved first) reloads the latest revision, keeps the
 * admin's edits on screen under a notice that takes the focus, and makes the next save ask for
 * confirmation saying it replaces the other admin's: nothing is ever overwritten silently. A
 * price change or a cap that becomes unset asks first too, showing the estimated maximum cost per
 * turn. A `422` lands on the field the server names; the save is sent once, never retried
 * (`retry: false`, checklist 13). Nothing is kept in browser storage.
 *
 * The action bar shows the server's figure for the stored settings and, when the admin asks for
 * it, the figure under the edits on screen ("Calcular con los cambios", one `POST` preview that
 * writes nothing and is never retried). A preview belongs to the values it was asked for
 * (`settingsKey`): any later edit drops it, and a late answer for older values is ignored.
 *
 * Mockup v3 P7: each tier picks its model from the provider catalogue
 * (`ModelCataloguePickerContainer`): the pick fills the tier and its prices (`fillFromCatalogue`)
 * as any edit does, and nothing is sent until the admin saves; a price typed over a catalogue one
 * is tagged «editado».
 */

import { useState } from "react";
import type { ReactElement } from "react";

import type { ApiError } from "../../../shared/api/ApiError";
import { useTranslation } from "../../../shared/i18n";
import { ConfirmDialog } from "../../../shared/ui/molecules/ConfirmDialog";
import { ErrorState } from "../../../shared/ui/molecules/ErrorState";
import { Skeleton } from "../../../shared/ui/atoms/Skeleton";
import { userMessage } from "../../../shared/ui/molecules/userMessage";
import type { EstimateRequest, ModelSettingsRequest, ModelSettingsView, TierName } from "../../../types/modelSettings";
import styles from "../components/ModelSettings.module.css";
import { ModelSettingsForm } from "../components/ModelSettingsForm";
import { ModelSettingsHistory } from "../components/ModelSettingsHistory";
import { CostLine, ModelSettingsSummary } from "../components/ModelSettingsSummary";
import { useEstimate, useModelSettings, useReloadModelSettings, useRestoreEnv, useRevisions, useSaveModelSettings } from "../hooks/useModelSettings";
import {
  chosenModels,
  confirmReasons,
  draftOf,
  editableOf,
  parseDraft,
  priceField,
  serverErrors,
  settingsKey,
  shownFields,
  suggestedModels,
} from "../model/modelSettingsForm";
import type { FieldErrors, SettingsDraft, TierDraft } from "../model/modelSettingsForm";
import { costText } from "../model/money";
import { editPrice, fillFromCatalogue } from "../model/catalogueFill";
import { ModelCataloguePickerContainer } from "./ModelCataloguePickerContainer";

const CONFLICT = 409;
const UNPROCESSABLE = 422;
const UNAVAILABLE = 503;
/** The currency the estimate is kept in. */
const ESTIMATE_CURRENCY = "USD";

type Confirming = { readonly kind: "save"; readonly body: ModelSettingsRequest } | { readonly kind: "restore" } | null;

/** The preview body: the `PUT` body without the revision. */
function previewOf(body: ModelSettingsRequest): EstimateRequest {
  return { tiers: body.tiers, prices: body.prices, message_max_chars: body.message_max_chars };
}

/** A preview of the cost under some values: pending, failed, or the server's figure. */
interface Preview {
  readonly key: string;
  readonly result: number | "pending" | "failed";
}

function without(errors: FieldErrors, keys: readonly string[]): FieldErrors {
  return Object.fromEntries(Object.entries(errors).filter(([key]) => !keys.includes(key)));
}

/** The two price fields of a model. */
function pricesOf(model: string): string[] {
  return [priceField(model, "prompt"), priceField(model, "completion")];
}

/** The form fields a tier edit clears the sentence of. */
function editedKeys(tier: TierName, patch: Partial<TierDraft>): string[] {
  return Object.keys(patch).flatMap((key) => {
    if (key === "model") return [`${tier}.model`, "capable.model"];
    return key === "effort" ? [`${tier}.effort`] : [`${tier}.cap`];
  });
}

interface EditorProps {
  readonly stored: ModelSettingsView;
  /** Reads the settings again: what a `409` is answered with. `undefined` when the read failed (the refusal is shown). */
  readonly reload: () => Promise<ModelSettingsView | undefined>;
}

/** The form over settings that have been read: the draft starts from them. */
function Editor({ stored, reload }: EditorProps): ReactElement {
  const { t } = useTranslation();
  const save = useSaveModelSettings();
  const restore = useRestoreEnv();
  const estimate = useEstimate();
  /** The action bar's own preview: another request than the confirmation's, asked for by the admin. */
  const barEstimate = useEstimate();
  const [preview, setPreview] = useState<Preview | null>(null);
  /** The settings the form was loaded from: its revision is what a save is based on. */
  const [base, setBase] = useState<ModelSettingsView>(stored);
  const [draft, setDraft] = useState<SettingsDraft>(() => draftOf(stored));
  const [errors, setErrors] = useState<FieldErrors>({});
  const [focusToken, setFocusToken] = useState(0);
  const [refusalToken, setRefusalToken] = useState(0);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [conflict, setConflict] = useState<number | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<Confirming>(null);

  function refused(fields: FieldErrors): void {
    setErrors(fields);
    setFocusToken((token) => token + 1);
  }

  function loaded(view: ModelSettingsView, message: string): void {
    setBase(view);
    setDraft(draftOf(view));
    setErrors({});
    setConflict(null);
    setSaved(message);
  }

  function failed(error: ApiError): void {
    if (error.status === CONFLICT) {
      // Somebody saved first: read the latest, keep the edits, and say so.
      void reload().then((latest) => {
        if (latest === undefined) {
          setRefusal(t("admin.modelSettings.failed"));
          return;
        }
        setBase(latest);
        setConflict(latest.revision);
      });
      return;
    }
    if (error.status === UNPROCESSABLE) {
      const shown = shownFields(draft);
      const fields = Object.fromEntries(Object.entries(serverErrors(error.details)).filter(([key]) => shown.has(key)));
      if (Object.keys(fields).length > 0) {
        setRefusal(t("admin.modelSettings.fixFields"));
        refused(fields);
        return;
      }
      // The server refused a field this form does not show: say so on the alert, which takes the focus.
      setRefusal(t("admin.modelSettings.failed"));
      setRefusalToken((token) => token + 1);
      return;
    }
    setRefusal(
      error.status === UNAVAILABLE
        ? t("admin.modelSettings.catalogueDown")
        : userMessage(error, t("admin.modelSettings.failed")),
    );
  }

  function send(body: ModelSettingsRequest): void {
    save.mutate(body, {
      onSuccess: (view) => {
        loaded(view, t("admin.modelSettings.saved", { revision: String(view.revision) }));
      },
      onError: failed,
    });
  }

  function submit(): void {
    setRefusal(null);
    setSaved(null);
    const parsed = parseDraft(draft, base);
    if (!parsed.ok) {
      refused(parsed.errors);
      return;
    }
    setErrors({});
    const reasons = confirmReasons(base, parsed.body);
    if (reasons.pricesChanged || reasons.capChanged || reasons.modelChanged || conflict !== null) {
      // The dialog shows what one turn can cost under THESE values: ask the server once, write nothing.
      estimate.mutate(previewOf(parsed.body));
      setConfirming({ kind: "save", body: parsed.body });
      return;
    }
    send(parsed.body);
  }

  function confirmed(): void {
    const pending = confirming;
    setConfirming(null);
    if (pending === null) return;
    if (pending.kind === "save") {
      send(pending.body);
      return;
    }
    restore.mutate(
      { expected_revision: base.revision },
      {
        onSuccess: (view) => {
          loaded(view, t("admin.modelSettings.restored", { revision: String(view.revision) }));
        },
        onError: failed,
      },
    );
  }

  function edited(keys: readonly string[]): void {
    setErrors((current) => without(current, keys));
    setSaved(null);
  }

  function costSentence(): string {
    if (estimate.isPending) return t("admin.modelSettings.confirm.costLoading");
    const micros = estimate.data?.estimated_max_cost_micros_per_turn ?? null;
    return micros === null
      ? t("admin.modelSettings.confirm.costUnknown")
      : t("admin.modelSettings.confirm.cost", { amount: costText(micros, ESTIMATE_CURRENCY) });
  }

  function reasonsText(body: ModelSettingsRequest): string {
    const reasons = confirmReasons(base, body);
    return [
      reasons.pricesChanged ? t("admin.modelSettings.confirm.prices") : null,
      reasons.capChanged && !reasons.capUnset ? t("admin.modelSettings.confirm.capChanged") : null,
      reasons.capUnset ? t("admin.modelSettings.confirm.capUnset") : null,
      reasons.modelChanged ? t("admin.modelSettings.confirm.model") : null,
      conflict === null ? null : t("admin.modelSettings.confirm.conflict"),
      costSentence(),
    ]
      .filter((sentence): sentence is string => sentence !== null)
      .join(" ");
  }

  // The edits on screen as the values a save would send, when they parse.
  const current = parseDraft(draft, base);
  const currentKey = current.ok ? settingsKey(previewOf(current.body)) : null;
  const changed = currentKey !== null && currentKey !== settingsKey(editableOf(base));
  const shownPreview = preview !== null && preview.key === currentKey ? preview.result : undefined;

  function askPreview(): void {
    if (!current.ok || currentKey === null) return;
    const key = currentKey;
    setPreview({ key, result: "pending" });
    const settle = (result: Preview["result"]): void => {
      // A late answer for values the admin has since edited is dropped (checklist 14).
      setPreview((latest) => (latest?.key === key ? { key, result } : latest));
    };
    barEstimate.mutate(previewOf(current.body), {
      onSuccess: (view) => {
        settle(view.estimated_max_cost_micros_per_turn ?? "failed");
      },
      onError: () => {
        settle("failed");
      },
    });
  }

  const suggestions = suggestedModels(base).map((model) => {
    const price = base.prices[model];
    return {
      value: model,
      label: model,
      description:
        price === undefined
          ? undefined
          : t("admin.modelSettings.suggestion", {
              prompt: costText(price.prompt_micros_per_million_tokens, price.currency),
              completion: costText(price.completion_micros_per_million_tokens, price.currency),
            }),
    };
  });

  const restoring = confirming?.kind === "restore";
  let dialogBody = "";
  if (confirming !== null) dialogBody = confirming.kind === "restore" ? t("admin.modelSettings.restore.body") : reasonsText(confirming.body);

  return (
    <>
      <ModelSettingsSummary settings={base} />
      <ModelSettingsForm
        draft={draft}
        models={chosenModels(draft)}
        suggestions={suggestions}
        limits={base.technical_limits}
        cost={
          <CostLine
            stored={base.estimated_max_cost_micros_per_turn}
            preview={shownPreview}
            canPreview={changed}
            onPreview={askPreview}
            money={(micros) => costText(micros, ESTIMATE_CURRENCY)}
          />
        }
        errors={errors}
        saving={save.isPending}
        restoring={restore.isPending}
        focusToken={focusToken}
        refusalToken={refusalToken}
        refusal={refusal}
        conflict={conflict}
        saved={saved}
        onTier={(tier, patch) => {
          setDraft((current) => ({ ...current, [tier]: { ...current[tier], ...patch } }));
          edited(editedKeys(tier, patch));
        }}
        onPrice={(model, part, value) => {
          setDraft((current) => editPrice(current, model, part, value));
          edited([priceField(model, part)]);
        }}
        renderPicker={(tier, close) => (
          <ModelCataloguePickerContainer
            selectedModelId={draft[tier].model}
            onPick={(model) => {
              setDraft((current) => fillFromCatalogue(current, tier, model).draft);
              edited([`${tier}.model`, "capable.model", `${tier}.cap`, `${tier}.effort`, ...pricesOf(model.model_id)]);
              close();
            }}
          />
        )}
        onMessage={(patch) => {
          setDraft((current) => ({
            ...current,
            message: patch.message ?? current.message,
            messageNoLimit: patch.noLimit ?? current.messageNoLimit,
          }));
          edited(["message"]);
        }}
        onSubmit={submit}
        onRestore={() => {
          setRefusal(null);
          setSaved(null);
          setConfirming({ kind: "restore" });
        }}
        onReload={() => {
          setDraft(draftOf(base));
          setErrors({});
          setConflict(null);
          setRefusal(null);
        }}
      />
      <ConfirmDialog
        open={confirming !== null}
        title={restoring ? t("admin.modelSettings.restore.title") : t("admin.modelSettings.confirm.title")}
        body={dialogBody}
        confirmLabel={restoring ? t("admin.modelSettings.restore.confirm") : t("admin.modelSettings.confirm.confirm")}
        cancelLabel={t("admin.modelSettings.confirm.cancel")}
        confirmLoading={confirming?.kind === "save" && estimate.isPending}
        onConfirm={confirmed}
        onCancel={() => {
          setConfirming(null);
        }}
      />
    </>
  );
}

/** The page while the settings are read: the shape of the tier cards, announced once. */
function SettingsSkeleton(): ReactElement {
  const { t } = useTranslation();
  return (
    <div className={styles.skeleton} role="status" aria-busy="true" aria-label={t("admin.modelSettings.loading")}>
      <Skeleton shape="block" />
      <div className={styles.tiers}>
        <Skeleton shape="block" />
        <Skeleton shape="block" />
      </div>
      <Skeleton shape="block" />
    </div>
  );
}

export function ModelSettingsContainer(): ReactElement {
  const settings = useModelSettings();
  const revisions = useRevisions();
  const reload = useReloadModelSettings();

  // The editor shows whenever settings were read: a failed re-read must never take the draft away.
  if (settings.data === undefined && settings.isError) {
    return (
      <ErrorState
        error={settings.error}
        onRetry={() => {
          void settings.refetch();
        }}
      />
    );
  }
  if (settings.data === undefined) return <SettingsSkeleton />;
  return (
    <div className={styles.layout}>
      <div className={styles.main}>
        <Editor stored={settings.data} reload={reload} />
      </div>
      <div className={styles.aside}>
        <ModelSettingsHistory
          items={revisions.data?.items}
          error={revisions.error}
          onRetry={() => {
            void revisions.refetch();
          }}
        />
      </div>
    </div>
  );
}
